import * as THREE from 'three';
import { LITE } from '../lite';
import { loadHumanData, type ArrayRef, type HumanData, type ModifierDef, type ProxyDef } from './data';
import { ageToYears, DEFAULT_MACROS, macroTargetWeights, type Macros } from './macro';
import { headDirs, attachPaint, bodyBounds, coverage, headFrame, makeupUniforms, renderMakeupMask, renderPaintMask, stockingCover, type Beard, type Makeup, type PaintedHair, type Stockings } from './paint';
import { printedTexture } from './patterns';
import { hairdoById } from './hairdo';
import { retry } from '../engine/retry';

/**
 * A parametric human: MakeHuman base mesh + macro targets (gender/age/race/build/height) +
 * modeling modifiers (face and body), with fitted proxies (eyes, brows, lashes, teeth, hair,
 * clothes). Shape is recomputed on the CPU whenever a parameter changes; positions are in metres
 * with the feet on y = 0 and the face towards +z.
 */

export interface Appearance {
  macros: Macros;
  modifiers: Record<string, number>;
  hair: string | null;
  /** close-cropped styles painted onto the scalp (used when `hair` is null or alongside it) */
  paintedHair: PaintedHair;
  beard: Beard;
  hairColour: string; // css colour multiplied into the hair texture, and used for painted hair/beard
  eyebrows: string | null;
  eyelashes: string | null;
  eyes: string; // eye colour key
  clothes: string[];
  /** skin tone fine-tune: multiplies the chosen skin texture (1 = as authored) */
  skinTone: number;
  /** fabric prints on garments (proxy name → print id), e.g. an ankara dress */
  prints?: Record<string, string>;
  /** accessories made in code (human/accessories.ts ids), worn on the head or neck */
  accessories?: string[];
  /** makeup on the face (colours; none when missing) */
  makeup?: Makeup;
  /** jewellery (human/jewellery.ts ids): kept apart from the outfit, so changing clothes leaves it on */
  jewellery?: string[];
  /** a fishnet body stocking painted on the skin (always worn under clothes) */
  stockings?: Stockings;
  /** done nails (the nail salon): their length and polish, kept apart from the outfit like jewellery */
  nails?: Nails;
}
/** MakeHuman's own hairstyles (the eye trim is for these) */
const STOCK_HAIR = new Set(['short01', 'short02', 'short03', 'short04', 'braid01', 'bob01', 'bob02', 'ponytail01', 'long01', 'afro01', 'taper01', 'locs01']);
/** a hair colour that keeps the hairstyle's own colours (its texture, not greyed and tinted) */
export const ORIGINAL = 'original';
export interface Nails { length: 'short' | 'medium' | 'long'; colour: string; /** the toes done too */ toes?: boolean }
/** the nail meshes (MakeHuman community bodyparts04, CC0): one on each fingertip, its own surface, so the polish shows */
export const NAIL_MESH: Record<Nails['length'], string> = { short: 'mindfront_nails_01_short', medium: 'mindfront_nails_02_medium', long: 'mindfront_nails_03_long' };
const nailMeshes = (a: Appearance) => (a.nails ? [NAIL_MESH[a.nails.length], ...(a.nails.toes ? ['mindfront_nails_toes_01'] : [])] : []);

export const DEFAULT_APPEARANCE: Appearance = {
  macros: { ...DEFAULT_MACROS },
  modifiers: {},
  hair: null,
  paintedHair: 'lowcut',
  beard: 'none',
  hairColour: '#120d0a',
  eyebrows: 'eyebrow001',
  eyelashes: 'eyelashes01',
  eyes: 'darkbrown',
  clothes: ['male_casualsuit01', 'shoes02'],
  skinTone: 1,
};

const SCALE = 0.1; // MakeHuman decimetres -> metres

export interface HumanPart {
  kind: 'body' | ProxyDef['kind'];
  mesh: THREE.Mesh;
  /** render vertex -> source vertex (body: base mesh vertex; proxy: proxy vertex) */
  renderToSrc: Uint16Array;
  /** proxies only: the three body vertices (and weights) each proxy vertex follows */
  refs?: Uint16Array;
  weights?: Float32Array;
}

interface ProxyMesh {
  def: ProxyDef;
  /** print currently applied (if any) */
  print?: string;
  mesh: THREE.Mesh;
  refs: Uint16Array;
  weights: Float32Array;
  offsets: Float32Array;
  renderToSrc: Uint16Array;
  index: Uint32Array;
  src: Float32Array; // fitted proxy vertices (metres)
  srcNormal: Float32Array;
}

export class Human {
  readonly group = new THREE.Group();
  readonly data: HumanData;
  appearance: Appearance;
  private src: Float32Array; // morphed base vertices, MakeHuman units
  private body: THREE.Mesh<THREE.BufferGeometry, THREE.MeshStandardMaterial>;
  private bodyR2S: Uint16Array;
  private bodyIndexAll: Uint32Array;
  private srcNormal: Float32Array;
  private proxies = new Map<string, ProxyMesh>();
  private textures = new Map<string, Promise<THREE.Texture>>();
  /** a light body (another player, seen from a few metres): its pictures shared with every other light body and smaller */
  private lite = false;
  private held: string[] = [];
  /** every picture asked for (skin, clothes, prints, hair): settled() waits for them all */
  private loads = new Set<Promise<unknown>>();
  /** resolves once every texture asked for so far is on (a photo taken before then shows white skin and clothes) */
  async settled() { let n = -1; while (this.loads.size !== n) { n = this.loads.size; await Promise.allSettled([...this.loads]); } }
  /** the proxies still downloading (a garment just picked), by name */
  private fetching = new Map<string, Promise<void>>();
  /** resolves once everything worn is on the body (skin a copy after this, or the new garment isn't on it) */
  async dressed() { while (this.fetching.size) await Promise.allSettled([...this.fetching.values()]); }
  private modifierDefs: Map<string, ModifierDef>;
  private offsetY = 0;
  private renderer?: THREE.WebGLRenderer;
  private paintMask = { value: null as THREE.Texture | null };
  private paintColour = { value: new THREE.Color() };
  private paintRT?: THREE.WebGLRenderTarget;
  /** the painted-hair mask's render target: kept per person, so freed with them */
  dispose() {
    this.paintRT?.dispose(); this.paintRT = undefined;
    this.mkRT?.dispose(); this.mkRT = undefined;
    // (a shared picture goes when the last light body wearing it does)
    for (const key of this.held.splice(0)) { const e = SHARED.get(key); if (e && --e.n <= 0) { SHARED.delete(key); void e.p.then((t) => t.dispose()).catch(() => {}); } }
  }
  private paintKey = '';
  private mk = makeupUniforms();
  private mkRT?: THREE.WebGLRenderTarget;
  private mkKey = '';
  /** the eyeballs' boxes (metres) as last fitted: where the makeup goes */
  private eyesM: { x0: number; x1: number; y0: number; y1: number; z: number }[] | null = null;
  private lips: Float32Array | null = null;
  /** how much each base vertex belongs to the left (0) and right (1) breast bones: for the bust control */
  private breasts: [Float32Array, Float32Array] | null = null;
  private breastWeights(): [Float32Array, Float32Array] {
    if (this.breasts) return this.breasts;
    const rig = this.data.manifest.rig, idx = this.data.view(rig.skinIndex) as Uint8Array, w = this.data.view(rig.skinWeight) as Float32Array;
    const L = rig.bones.findIndex((b) => b.name === 'breast.L'), R = rig.bones.findIndex((b) => b.name === 'breast.R');
    const n = this.data.manifest.vertexCount, out: [Float32Array, Float32Array] = [new Float32Array(n), new Float32Array(n)];
    for (let v = 0; v < n; v++) for (let k = 0; k < 4; k++) {
      const b = idx[v * 4 + k];
      if (b === L) out[0][v] += w[v * 4 + k]; else if (b === R) out[1][v] += w[v * 4 + k];
    }
    return (this.breasts = out);
  }
  /**
   * The bust (modifiers['custom/bust'], -1..1): there's no MakeHuman target for it, so each breast is shaped
   * along its own bone's weights: fuller swells out from its centre and forward, smaller flattens toward the chest.
   * Clothes follow, being fitted to the body after this.
   */
  private shapeBust(src: Float32Array) {
    const amt = this.appearance.modifiers['custom/bust'] ?? 0;
    if (Math.abs(amt) < 1e-3) return;
    const ws = this.breastWeights();
    for (const w of ws) {
      // the breast's centre and its forward-most point, weighted by how much each vertex belongs to it
      let cx = 0, cy = 0, cz = 0, t = 0, front = -Infinity;
      for (let v = 0; v < w.length; v++) if (w[v] > 0.3) { cx += src[v * 3] * w[v]; cy += src[v * 3 + 1] * w[v]; cz += src[v * 3 + 2] * w[v]; t += w[v]; front = Math.max(front, src[v * 3 + 2]); }
      if (!t) continue;
      cx /= t; cy /= t; cz /= t;
      const depth = Math.max(0.2, front - cz);
      for (let v = 0; v < w.length; v++) {
        const k = w[v]; if (k < 0.02) continue;
        // (the falloff: full at the bone's own vertices, nothing at the edge of its weights: no crease)
        const f = amt * k * k * (3 - 2 * k);
        const dz = src[v * 3 + 2] - cz, ahead = Math.max(0, Math.min(1, dz / depth + 0.5));
        src[v * 3] += (src[v * 3] - cx) * 0.22 * f;
        src[v * 3 + 1] += (src[v * 3 + 1] - cy) * 0.18 * f - 0.05 * Math.max(0, f) * ahead; // (fuller sits a touch lower)
        src[v * 3 + 2] += (dz * 0.35 + 0.28 * ahead) * f;
      }
    }
  }
  /** How much each base vertex is lip (0..1), from the face rig: the weight it carries on the orbicularis oris
   *  bones (oris*). The beard never paints there, whatever the beard's shape says. */
  private lipMask(): Float32Array {
    if (this.lips) return this.lips;
    const rig = this.data.manifest.rig, idx = this.data.view(rig.skinIndex) as Uint8Array, w = this.data.view(rig.skinWeight) as Float32Array;
    const oris = new Set(rig.bones.map((b, i) => (b.name.startsWith('oris') ? i : -1)).filter((i) => i >= 0));
    const n = this.data.manifest.vertexCount, out = new Float32Array(n);
    for (let v = 0; v < n; v++) {
      let t = 0;
      for (let k = 0; k < 4; k++) if (oris.has(idx[v * 4 + k])) t += w[v * 4 + k];
      out[v] = Math.min(1, Math.max(0, (t - 0.55) / 0.3)); // (the lips themselves, not the skin round them)
    }
    return (this.lips = out);
  }

  /** The renderer is needed to paint close-cropped hair and beards into a mask texture. */
  static async create(appearance: Appearance = DEFAULT_APPEARANCE, renderer?: THREE.WebGLRenderer, o: { lite?: boolean } = {}): Promise<Human> {
    const data = await loadHumanData(), a = { ...DEFAULT_APPEARANCE, ...appearance };
    // (what they're wearing, fetched first: a body is skinned from what it has when it's made)
    await Promise.all(Human.wanted(a).map((n) => data.manifest.proxies.find((p) => p.name === n)).filter((d): d is ProxyDef => !!d).map((d) => data.loadProxy(d).catch(() => { /* drawn without it */ })));
    const h = new Human(data, a, renderer, !!(o.lite ?? LITE)); // (Lite: everyone on the small textures, you too)
    return h;
  }
  /** the proxies an appearance needs (as apply() will read it: the afro is the taper now, the polo the tee) */
  private static wanted(a: Appearance): string[] {
    const hair = a.hair === 'afro01' ? 'taper01' : a.hair;
    const clothes = (a.clothes ?? []).map((c) => (c === 'namuhekam_male_polo_shirt' ? 'toigo_basic_tucked_t-shirt' : c));
    return [hair, a.eyebrows, a.eyelashes, 'high-poly', 'teeth_base', ...clothes, ...nailMeshes(a)].filter((x): x is string => !!x && !x.startsWith('code:')); // (code: hair is built strand by strand, hairdo.ts)
  }

  private constructor(data: HumanData, appearance: Appearance, renderer?: THREE.WebGLRenderer, lite = false) {
    this.lite = lite;
    this.renderer = renderer;
    this.data = data;
    this.appearance = structuredClone(appearance);
    const V = data.manifest.vertexCount;
    this.src = new Float32Array(V * 3);
    this.srcNormal = new Float32Array(V * 3);
    this.modifierDefs = new Map(data.manifest.modifiers.map((m) => [m.id, m]));

    const b = data.manifest.body;
    this.bodyR2S = data.view(b.renderToSrc) as Uint16Array;
    this.bodyIndexAll = data.view(b.index) as Uint32Array;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(this.bodyR2S.length * 3), 3));
    geo.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(this.bodyR2S.length * 3), 3));
    geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(data.view(b.uv) as Float32Array), 2));
    geo.setIndex(new THREE.BufferAttribute(this.bodyIndexAll.slice(), 1));
    this.body = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ roughness: 0.62, metalness: 0, envMapIntensity: 0.8 }));
    attachPaint(this.body.material, this.paintMask, this.paintColour, this.mk);
    this.body.name = 'body';
    this.body.castShadow = this.body.receiveShadow = true;
    this.group.add(this.body);
    this.apply(this.appearance);
  }

  modifiers(): ModifierDef[] { return this.data.manifest.modifiers; }

  /** Set any subset of the appearance and rebuild what changed. */
  apply(next: Partial<Appearance>) {
    this.appearance = { ...this.appearance, ...structuredClone(next) };
    if (this.appearance.hair === 'afro01') this.appearance.hair = 'taper01'; // (the afro was replaced by the taper: saved characters move over)
    // (the polo's mesh has holes at the hem, the trousers show through: anyone wearing it gets the plain tee instead)
    if (this.appearance.clothes?.includes('namuhekam_male_polo_shirt')) this.appearance.clothes = this.appearance.clothes.map((c) => (c === 'namuhekam_male_polo_shirt' ? 'toigo_basic_tucked_t-shirt' : c));
    this.morph();
    this.syncProxies();
    this.fitProxies();
    this.updateSkin();
    this.updatePaint();
  }

  // --- painted hair and beard ------------------------------------------------------

  /**
   * Makeup: a mask in the body's UV space from where each vertex sits on the face. Lips from the lip bones; eyeshadow
   * an oval over each upper lid; liner a thin band at the lash line; blush a soft round on each cheek's apple.
   */
  private updateMakeup() {
    const m = this.appearance.makeup ?? {}, eyes = this.eyesM;
    const on = !!(m.lips || m.shadow || m.blush || m.liner);
    this.mk.amt.value.set(m.lips ? 0.72 : 0, m.shadow ? 0.6 : 0, m.blush ? 0.65 : 0, m.liner ? 0.92 : 0);
    if (m.lips) this.mk.lips.value.set(m.lips);
    if (m.shadow) this.mk.shadow.value.set(m.shadow);
    if (m.blush) this.mk.blush.value.set(m.blush);
    this.mk.gloss.value = m.gloss ? 1 : 0;
    if (!on || !eyes || !this.renderer) { this.mk.mask.value = null; return; }
    // (the mask only changes with the face's shape: the colours are uniforms)
    const e0 = eyes[0], key = `${Object.entries(this.appearance.modifiers).map(([k, v]) => k + v.toFixed(2)).join()}|${this.appearance.macros.age.toFixed(2)}|${this.appearance.macros.gender.toFixed(2)}|${e0.x0.toFixed(4)}`;
    if (key !== this.mkKey || !this.mkRT) {
      this.mkKey = key;
      const n = this.bodyR2S.length, cover = new Float32Array(n * 4), lips = this.lipMask(), P = new THREE.Vector3();
      // the lips' own outline: an oval round the vertices the lip bones carry fully (lipstick never spills onto the skin
      // round the mouth: that reads as bigger lips, and makeup only colours)
      let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
      for (let i = 0; i < n; i++) { const v = this.bodyR2S[i]; if (lips[v] < 0.999) continue; this.vertex(v, P); x0 = Math.min(x0, P.x); x1 = Math.max(x1, P.x); y0 = Math.min(y0, P.y); y1 = Math.max(y1, P.y); }
      const lcx = (x0 + x1) / 2, lcy = (y0 + y1) / 2, lw = Math.max(0.005, (x1 - x0) / 2) * 0.92, lh = Math.max(0.003, (y1 - y0) / 2) * 0.82;
      for (let i = 0; i < n; i++) {
        const v = this.bodyR2S[i];
        this.vertex(v, P);
        const e = ((P.x - lcx) / lw) ** 2 + ((P.y - lcy) / lh) ** 2;
        cover[i * 4] = lips[v] > 0.5 ? Math.min(1, Math.max(0, (1 - e) / 0.3)) : 0;
        for (const e of eyes) {
          if (P.z < e.z - 0.035) continue;
          const cx = (e.x0 + e.x1) / 2, w = (e.x1 - e.x0) / 2, cy = (e.y0 + e.y1) / 2, h = (e.y1 - e.y0) / 2, out = Math.sign(cx);
          const ex = (P.x - cx) / (w * 1.45), dy = P.y - cy;
          // eyeshadow: over the lid, up to the crease, swept a little out at the corner
          const ey = (dy - h * 0.58) / (h * 0.42), sx = ex - out * 0.12 * Math.max(0, dy / h);
          const sh = 1 - (sx * sx + ey * ey);
          if (sh > 0 && dy > h * 0.2) cover[i * 4 + 1] = Math.max(cover[i * 4 + 1], Math.min(1, sh * 1.8));
          // liner: the lash line, thickening toward the outer corner
          const lx = (P.x - cx) / w, band = Math.abs(dy - h * 0.42) / (h * (0.16 + 0.12 * Math.max(0, lx * out)));
          if (band < 1 && Math.abs(lx) < 1.05) cover[i * 4 + 3] = Math.max(cover[i * 4 + 3], 1 - band * band);
          // blush: the apple of the cheek, below and a little out from the eye
          const bx = P.x - (cx + out * w * 0.5), by = P.y - (cy - 0.034), bd = (bx * bx + by * by * 1.3) / (0.022 * 0.022);
          if (bd < 4) cover[i * 4 + 2] = Math.max(cover[i * 4 + 2], Math.exp(-bd));
        }
      }
      const uv = this.body.geometry.getAttribute('uv').array as Float32Array;
      this.mkRT = renderMakeupMask(this.renderer, uv, new THREE.BufferAttribute(this.bodyIndexAll, 1), cover, this.mkRT);
    }
    this.mk.mask.value = this.mkRT.texture;
  }

  private updatePaint() {
    this.updateMakeup();
    const a = this.appearance;
    this.paintColour.value.set(a.hairColour === ORIGINAL ? '#1a1410' : a.hairColour);
    if (hairdoById(a.hair)) this.paintColour.value.multiplyScalar(0.45); // (the scalp under braids or a wig: darker than the hair, so the partings read as shadow)
    // under the afro the hairline is painted in too: the shell meets the scalp there, and the paint makes it a hairline
    const code = hairdoById(a.hair);
    const painted = a.paintedHair === 'none' && a.hair === 'afro01' ? 'afro' : a.paintedHair === 'none' && a.hair === 'taper01' ? 'taper' : a.paintedHair === 'none' && a.hair === 'locs01' ? 'afro' : code ? code.scalp : a.paintedHair; // (braids are put in over cornrows; a wig sits on a close crop)
    const key = `${painted}|${a.beard}|${a.stockings ?? 'none'}`;
    if (key === this.paintKey || !this.renderer) return;
    this.paintKey = key;
    const frame = headFrame(this.data.base, this.bodyR2S);
    const cover = new Float32Array(this.bodyR2S.length * 3);
    const legs = a.stockings === 'fishnet' ? bodyBounds(this.data.base, this.bodyR2S) : null;
    for (let i = 0; i < this.bodyR2S.length; i++) {
      const [h, b] = coverage(this.data.base, this.bodyR2S[i], frame, painted, a.beard);
      cover[i * 3] = h; cover[i * 3 + 1] = b * (1 - this.lipMask()[this.bodyR2S[i]]); cover[i * 3 + 2] = legs ? stockingCover(this.data.base, this.bodyR2S[i], legs) : 0;
    }
    const uv = this.body.geometry.getAttribute('uv').array as Float32Array;
    this.paintRT = renderPaintMask(this.renderer, uv, new THREE.BufferAttribute(this.bodyIndexAll, 1), cover, painted, a.beard, this.paintRT, painted === 'taper' || painted === 'cornrows' ? headDirs(this.data.base, this.bodyR2S, frame) : undefined);
    this.paintMask.value = painted === 'none' && a.beard === 'none' && (a.stockings ?? 'none') === 'none' ? null : this.paintRT.texture;
    this.body.material.needsUpdate = true;
  }

  // --- shape ---------------------------------------------------------------------

  private activeTargets(): Map<string, number> {
    const w = macroTargetWeights(this.appearance.macros);
    for (const [id, v] of Object.entries(this.appearance.modifiers)) {
      const def = this.modifierDefs.get(id);
      if (!def || Math.abs(v) < 1e-4) continue;
      const names = def.bipolar ? (v < 0 ? def.min : def.max) : def.max;
      for (const n of names) w.set(n, (w.get(n) ?? 0) + Math.abs(v));
    }
    return w;
  }

  private morph() {
    const { base, targetIndex, targetDelta, targets } = this.data;
    const src = this.src;
    src.set(base);
    const k = 1 / this.data.manifest.deltaScale;
    for (const [name, w] of this.activeTargets()) {
      // (Unfold Lite's core has no baby or child shapes: their weight goes to the young one instead)
      const t = targets.get(name) ?? (LITE ? targets.get(name.replace(/-(baby|child)(?=-|$)/, '-young')) : undefined);
      if (!t) continue;
      const f = w * k;
      for (let i = 0; i < t.count; i++) {
        const v = targetIndex[t.start + i] * 3, d = (t.start + i) * 3;
        src[v] += targetDelta[d] * f;
        src[v + 1] += targetDelta[d + 1] * f;
        src[v + 2] += targetDelta[d + 2] * f;
      }
    }
    this.shapeBust(src);
    // feet on the ground: lowest body vertex goes to y = 0
    let minY = Infinity;
    for (let i = 0; i < this.bodyR2S.length; i++) minY = Math.min(minY, src[this.bodyR2S[i] * 3 + 1]);
    this.offsetY = -minY * SCALE;

    // smooth normals accumulated on source vertices, so UV seams don't show as hard edges
    accumulateNormals(src, this.bodyIndexAll, this.bodyR2S, this.srcNormal);
    const pos = this.body.geometry.getAttribute('position') as THREE.BufferAttribute;
    const nrm = this.body.geometry.getAttribute('normal') as THREE.BufferAttribute;
    const P = pos.array as Float32Array, N = nrm.array as Float32Array;
    for (let i = 0; i < this.bodyR2S.length; i++) {
      const s = this.bodyR2S[i] * 3;
      P[i * 3] = src[s] * SCALE; P[i * 3 + 1] = src[s + 1] * SCALE + this.offsetY; P[i * 3 + 2] = src[s + 2] * SCALE;
      N[i * 3] = this.srcNormal[s]; N[i * 3 + 1] = this.srcNormal[s + 1]; N[i * 3 + 2] = this.srcNormal[s + 2];
    }
    pos.needsUpdate = nrm.needsUpdate = true;
    this.body.geometry.computeBoundingSphere();
    this.body.geometry.computeBoundingBox();
  }

  /** Morphed base vertex in metres (used by the rig and by camera framing). */
  vertex(i: number, out = new THREE.Vector3()): THREE.Vector3 {
    return out.set(this.src[i * 3] * SCALE, this.src[i * 3 + 1] * SCALE + this.offsetY, this.src[i * 3 + 2] * SCALE);
  }

  /** The body and fitted proxies in their current shape, for skinning (see rig.ts). */
  parts(): HumanPart[] {
    const out: HumanPart[] = [{ kind: 'body', mesh: this.body, renderToSrc: this.bodyR2S }];
    for (const p of this.proxies.values()) out.push({ kind: p.def.kind, mesh: p.mesh, renderToSrc: p.renderToSrc, refs: p.refs, weights: p.weights });
    return out;
  }

  /** Height of the top of the head in metres. */
  height(): number { return this.body.geometry.boundingBox?.max.y ?? 1.75; }

  // --- proxies -------------------------------------------------------------------------

  private wantedProxies(): string[] {
    const a = this.appearance;
    return [a.hair, a.eyebrows, a.eyelashes, 'high-poly', 'teeth_base', ...a.clothes, ...nailMeshes(a)].filter((x): x is string => !!x && !x.startsWith('code:'));
  }

  private syncProxies() {
    const want = new Set(this.wantedProxies());
    for (const [name, p] of this.proxies) {
      if (!want.has(name)) { this.group.remove(p.mesh); p.mesh.geometry.dispose(); this.proxies.delete(name); }
    }
    for (const name of want) if (!this.proxies.has(name)) this.addProxy(name);
    this.paintNails();
    // prints changed on garments already worn
    for (const [name, p] of this.proxies) {
      const print = this.appearance.prints?.[name];
      if (print === p.print) continue;
      p.print = print;
      const mat = p.mesh.material as THREE.MeshStandardMaterial;
      const tex = p.def.texture;
      this.loads.add((print ? printedTexture(tex ? this.data.url(tex) : null, print) : tex ? this.texture(tex) : Promise.resolve(null))
        .then((t) => { if (t && p.print === print) { mat.map = t; mat.needsUpdate = true; } }));
    }
    // hide skin under clothes
    const hidden = new Set<number>();
    for (const name of this.appearance.clothes) for (const v of this.proxies.get(name)?.def.deleteVerts ?? []) hidden.add(v);
    const all = this.bodyIndexAll, r2s = this.bodyR2S;
    const kept: number[] = [];
    for (let i = 0; i < all.length; i += 3) {
      if (hidden.has(r2s[all[i]]) || hidden.has(r2s[all[i + 1]]) || hidden.has(r2s[all[i + 2]])) continue;
      kept.push(all[i], all[i + 1], all[i + 2]);
    }
    this.body.geometry.setIndex(kept);
    // hair colour and eye colour live on the proxy materials
    const hair = this.appearance.hair && this.proxies.get(this.appearance.hair);
    if (hair) {
      const mat = hair.mesh.material as THREE.MeshStandardMaterial, raw = this.appearance.hairColour === ORIGINAL;
      mat.color.set(raw ? '#ffffff' : this.appearance.hairColour);
      // (to or from its own colours: the picture again, greyed or not)
      if (!!mat.userData.raw !== raw && hair.def.texture) { mat.userData.raw = raw; const tx = hair.def.texture; this.loads.add(this.texture(tx, true, raw).then((t) => { if ((this.appearance.hairColour === ORIGINAL) === raw) { mat.map = t; mat.needsUpdate = true; } })); }
    }
    const eyes = this.proxies.get('high-poly');
    const eyeTex = this.data.manifest.eyeColours[this.appearance.eyes];
    if (eyes && eyeTex) this.texture(eyeTex).then((t) => { (eyes.mesh.material as THREE.MeshStandardMaterial).map = t; (eyes.mesh.material as THREE.MeshStandardMaterial).needsUpdate = true; });
  }

  private addProxy(name: string) {
    const def = this.data.manifest.proxies.find((p) => p.name === name);
    if (!def) return;
    const d = this.data;
    if (!d.hasProxy(def)) {
      // (not downloaded yet: a garment picked in the creator, a hairstyle tried on. It comes on when it's here)
      if (this.fetching.has(name)) return;
      const p = d.loadProxy(def).then(() => { if (this.wantedProxies().includes(name) && !this.proxies.has(name)) this.apply({}); }, () => { /* without it */ })
        .finally(() => this.fetching.delete(name));
      this.fetching.set(name, p);
      this.loads.add(p);
      return;
    }
    const v = (ref: ArrayRef) => d.proxyView(def, ref);
    const renderToSrc = v(def.renderToSrc) as Uint16Array;
    const refs = v(def.refs) as Uint16Array;
    const n = refs.length / 3;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(renderToSrc.length * 3), 3));
    geo.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(renderToSrc.length * 3), 3));
    geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(v(def.uv) as Float32Array), 2));
    const index = v(def.index) as Uint32Array;
    geo.setIndex(new THREE.BufferAttribute(index.slice(), 1));
    // eyes count too: their outer cornea shell maps to a transparent patch and must be cut away
    const alpha = def.transparent || def.kind === 'hair' || def.kind === 'eyebrows' || def.kind === 'eyelashes' || def.kind === 'eyes';
    const mat = new THREE.MeshStandardMaterial({
      roughness: def.kind === 'eyes' ? 0.15 : def.kind === 'hair' ? 0.75 : 0.85,
      // (clothes from both sides: a hem or a cuff seen from below shows its inside, not a hole through to the dark)
      side: (alpha && def.kind !== 'eyes') || def.kind === 'clothes' ? THREE.DoubleSide : THREE.FrontSide,
      alphaTest: alpha ? 0.45 : 0,
      transparent: false,
    });
    const print = this.appearance.prints?.[name];
    if (print) printedTexture(def.texture ? this.data.url(def.texture) : null, print).then((t) => { mat.map = t; mat.needsUpdate = true; });
    else if (def.texture && def.kind !== 'eyes') { const raw = def.kind === 'hair' && this.appearance.hairColour === ORIGINAL; mat.userData.raw = raw; this.texture(def.texture, true, raw).then((t) => { mat.map = t; mat.needsUpdate = true; }); }
    // hair as a renderer like Blender draws it: the strands dithered in and out by their alpha (soft, wispy edges, no
    // sorting; the hard cut made every style a stack of plastic panels), the pack's normal map for the strands'
    // relief, and a hair's sheen. (The light bodies, other players far off, keep the cheap cut)
    // hair as a renderer like Blender draws it: silky. The solid core drawn hard, then the strands' fading ends blended
    // softly over it (the hard cut alone made every style a stack of plastic panels), with a sheen that runs along the
    // strands (anisotropy) and the pack's normal map. The light bodies (other players far off) keep the cheap cut
    let soft: THREE.MeshStandardMaterial | null = null;
    if (def.kind === 'hair' && !this.lite) {
      mat.alphaTest = 0.8; mat.roughness = 0.5;
      soft = new THREE.MeshStandardMaterial({ roughness: 0.8, side: THREE.DoubleSide, transparent: true, depthWrite: false, alphaTest: 0.02 });
      if (def.normal) this.texture(def.normal, true, true).then((t) => { t.colorSpace = THREE.NoColorSpace; mat.normalMap = t; mat.normalScale.set(0.35, 0.35); mat.needsUpdate = true; if (soft) { soft.normalMap = t; soft.normalScale.set(0.35, 0.35); soft.needsUpdate = true; } });
    }
    const mesh = new THREE.Mesh(geo, mat);
    mesh.name = `${def.kind}:${name}`;
    if (soft) {
      const s2 = soft, edge = new THREE.Mesh(geo, s2);
      edge.name = `${def.kind}:${name}:soft`; edge.renderOrder = def.zDepth + 1;
      edge.onBeforeRender = () => { if (s2.map !== mat.map) { s2.map = mat.map; s2.needsUpdate = true; } s2.color.copy(mat.color); };
      mesh.add(edge);
    }
    mesh.castShadow = def.kind !== 'eyelashes' && def.kind !== 'eyebrows';
    mesh.receiveShadow = true;
    mesh.renderOrder = def.zDepth; // MakeHuman's layering hint (skin < clothes < hair)
    this.group.add(mesh);
    this.proxies.set(name, {
      def, mesh, refs, renderToSrc, index, print,
      weights: v(def.weights) as Float32Array, offsets: v(def.offsets) as Float32Array,
      src: new Float32Array(n * 3), srcNormal: new Float32Array(n * 3),
    });
  }

  /** the polish: the nail meshes in its colour, with a shine (none: a natural nail, the skin's own pink) */
  private paintNails() {
    for (const [name, p] of this.proxies) {
      if (!/^mindfront_nails_/.test(name)) continue;
      const mat = p.mesh.material as THREE.MeshStandardMaterial, c = this.appearance.nails?.colour ?? '#e9b9a8';
      if (mat.userData.polish === c) continue;
      mat.userData.polish = c; mat.map = null; mat.color.set(c); mat.roughness = 0.18; mat.metalness = 0.05; mat.needsUpdate = true;
    }
  }
  /** how far a garment stands off what's under it (m): a few mm, more for each layer it's worn over */
  private layerLift(def: { kind: string; name: string; zDepth: number }): number {
    if (def.kind === 'hair') return /locs/.test(def.name) ? 0 : 0.006; // (hair a few mm off the scalp: right on it, the skin showed through in patches: the temples, the crown. Not the locs: tubes, each its own normals, they burst open)
    if (def.kind !== 'clothes') return 0;
    // (by what it is, not MakeHuman's zDepth, which puts some trousers outside the shirt: a top hangs over trousers and
    // skirts, unless it's tucked in)
    const n = def.name.toLowerCase();
    if (/shoe|boot|flats|uggs|sandal/.test(n)) return 0.001;
    if (/pants|trousers|skirt|shorts|jeans/.test(n)) return 0.004;
    if (/tucked/.test(n)) return 0.003;
    if (/shirt|polo|sweater|tee|top|hoodie|jacket|blouse|kimono|bodice|camisole/.test(n)) return 0.016;
    return 0.005;
  }

  private fitProxies() {
    const s = this.src;
    // (the eyes first: the hair is trimmed against them)
    let eyeBox: { x0: number; x1: number; y0: number; y1: number; z: number }[] | null = null;
    for (const p of [...this.proxies.values()].sort((a, b) => (a.def.kind === 'eyes' ? -1 : 0) - (b.def.kind === 'eyes' ? -1 : 0))) {
      const sc = { x: 1, y: 1, z: 1 };
      for (const axis of ['x', 'y', 'z'] as const) {
        const ref = p.def.scales[axis];
        if (!ref) continue;
        const ai = { x: 0, y: 1, z: 2 }[axis];
        if (ref[2] > 1e-6) sc[axis] = Math.abs(s[ref[0] * 3 + ai] - s[ref[1] * 3 + ai]) / ref[2]; // some assets ship a 0 reference
      }
      const n = p.refs.length / 3;
      for (let j = 0; j < n; j++) {
        const r0 = p.refs[j * 3] * 3, r1 = p.refs[j * 3 + 1] * 3, r2 = p.refs[j * 3 + 2] * 3;
        const w0 = p.weights[j * 3], w1 = p.weights[j * 3 + 1], w2 = p.weights[j * 3 + 2];
        for (let c = 0; c < 3; c++) {
          const off = p.offsets[j * 3 + c] * (c === 0 ? sc.x : c === 1 ? sc.y : sc.z);
          p.src[j * 3 + c] = (w0 * s[r0 + c] + w1 * s[r1 + c] + w2 * s[r2 + c] + off) * SCALE + (c === 1 ? this.offsetY : 0);
        }
      }
      if (p.def.kind === 'eyes') eyeBox = this.eyesM = eyeBoxes(p.src);
      // a haircut never covers the eyes: hair in front of an eye stops just above it (a bob
      // swept across the face hid a whole eye)
      // (only the game's own styles: the community's are made with locks across the face on purpose, and folding them up
      // above the eyes turned them into flat slabs)
      if (p.def.kind === 'hair' && eyeBox && STOCK_HAIR.has(p.def.name)) for (let j = 0; j < n; j++) {
        const x = p.src[j * 3], z = p.src[j * 3 + 2];
        for (const e of eyeBox) {
          if (z < e.z - 0.05) continue; // (the eye's outer corner sits well behind the cornea: a lock in front of it counts too)
          // (the whole strand below the line folds up into a thin band under it, blending off beside the eye: no holes,
          // no triangle left spanning the eye)
          const out = Math.max(0, (x < e.x0 ? e.x0 - x : x > e.x1 ? x - e.x1 : 0) - 0.03), w = Math.max(0, 1 - out / 0.02);
          const y = p.src[j * 3 + 1], limit = e.y1 + 0.003; // (above the eye: a haircut never covers the eyes)
          if (!w || y >= limit || y < e.y0 - 0.2) continue; // (down to the chin: a long lock's card may have no vertex beside the eye at all)
          p.src[j * 3 + 1] = y + w * (limit - (limit - y) * 0.03 - y);
        }
      }
      accumulateNormals(p.src, p.index, p.renderToSrc, p.srcNormal);
      const pos = p.mesh.geometry.getAttribute('position') as THREE.BufferAttribute;
      const nrm = p.mesh.geometry.getAttribute('normal') as THREE.BufferAttribute;
      const P = pos.array as Float32Array, N = nrm.array as Float32Array;
      // (clothes stand a hair off the body and off each other, the outer layer the most: else skin, or the trousers'
      // waistband, shows through a tight spot as holes in the shirt)
      const lift = this.layerLift(p.def);
      for (let i = 0; i < p.renderToSrc.length; i++) {
        const k = p.renderToSrc[i] * 3;
        P[i * 3] = p.src[k] + p.srcNormal[k] * lift; P[i * 3 + 1] = p.src[k + 1] + p.srcNormal[k + 1] * lift; P[i * 3 + 2] = p.src[k + 2] + p.srcNormal[k + 2] * lift;
        N[i * 3] = p.srcNormal[k]; N[i * 3 + 1] = p.srcNormal[k + 1]; N[i * 3 + 2] = p.srcNormal[k + 2];
      }
      pos.needsUpdate = nrm.needsUpdate = true;
      p.mesh.geometry.computeBoundingSphere();
    }
  }

  // --- skin ------------------------------------------------------------------------------

  /** Pick the authored skin nearest to the current age, race mix and gender. */
  skinFor(): string {
    const m = this.appearance.macros;
    const years = ageToYears(m.age);
    const age = years < 36 ? 'young' : years < 58 ? 'middleage' : 'old';
    const race = m.african >= m.asian && m.african >= m.caucasian ? 'african' : m.asian >= m.caucasian ? 'asian' : 'caucasian';
    const sex = m.gender >= 0.5 ? 'male' : 'female';
    const skins = this.data.manifest.skins;
    return (skins.find((s) => s.age === age && s.race === race && s.sex === sex) ?? skins[0]).texture;
  }

  private currentSkin = '';
  private updateSkin() {
    const t = this.skinFor();
    this.body.material.color.setScalar(this.appearance.skinTone);
    if (t === this.currentSkin) return;
    this.currentSkin = t;
    this.texture(t).then((tex) => { if (this.currentSkin === t) { this.body.material.map = tex; this.body.material.needsUpdate = true; } });
  }

  private texture(rel: string, flipY = true, raw = false): Promise<THREE.Texture> {
    const key = `${rel}|${flipY}${raw ? '|raw' : ''}`;
    // (hair is greyed so the chosen colour shows; 'original' keeps the hair's own colours: a pink wig stays pink)
    const hair = !raw && this.data.manifest.proxies.some((d) => d.kind === 'hair' && d.texture === rel);
    if (this.lite) {
      // one copy for all the light bodies, made smaller: a skin at 1024, the rest at 512 (each other player's own 2048
      // skin and 1024 clothes were ~40 MB of GPU apiece: a full Adeniran Street reloaded phones)
      const lk = `${key}|lite`;
      let e = SHARED.get(lk);
      if (!e) {
        const skin = this.data.manifest.skins.some((x) => x.texture === rel);
        e = { n: 0, p: retry(() => new THREE.TextureLoader().loadAsync(this.data.url(rel))).then((t0) => {
          const t = shrink(hair ? greyHair(t0) : t0, skin ? 1024 : 512);
          t.flipY = flipY; t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4;
          t.userData.shared = true; // (Npc.dispose leaves it: this cache frees it)
          return t;
        }) };
        SHARED.set(lk, e);
      }
      if (!this.held.includes(lk)) { e.n++; this.held.push(lk); }
      this.loads.add(e.p);
      return e.p;
    }
    let p = this.textures.get(key);
    if (!p) {
      p = retry(() => new THREE.TextureLoader().loadAsync(this.data.url(rel))).then((t0) => {
        const t = hair ? greyHair(t0) : t0;
        t.flipY = flipY;
        t.colorSpace = THREE.SRGBColorSpace; // OBJ UVs are bottom-left origin, matching three's default flipY
        t.anisotropy = 8;
        return t;
      });
      this.textures.set(key, p);
      this.loads.add(p);
    }
    return p;
  }
}

/** Area-weighted vertex normals accumulated per source vertex (shared across UV seams). */
function accumulateNormals(src: Float32Array, index: Uint32Array, r2s: Uint16Array, out: Float32Array) {
  out.fill(0);
  for (let i = 0; i < index.length; i += 3) {
    const a = r2s[index[i]] * 3, b = r2s[index[i + 1]] * 3, c = r2s[index[i + 2]] * 3;
    const ux = src[b] - src[a], uy = src[b + 1] - src[a + 1], uz = src[b + 2] - src[a + 2];
    const vx = src[c] - src[a], vy = src[c + 1] - src[a + 1], vz = src[c + 2] - src[a + 2];
    const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    for (const k of [a, b, c]) { out[k] += nx; out[k + 1] += ny; out[k + 2] += nz; }
  }
  for (let i = 0; i < out.length; i += 3) {
    const l = Math.hypot(out[i], out[i + 1], out[i + 2]);
    if (l > 0) { out[i] /= l; out[i + 1] /= l; out[i + 2] /= l; } else out[i + 1] = 1;
  }
}

/**
 * The hair textures come dark brown, and the chosen colour is multiplied into them: blonde times near-black is
 * still near-black (a faint tint, not a colour). So keep only the strand detail: a light grey, with the
 * brightest strands near white, and let the material colour be the hair colour.
 */
/** the light bodies' pictures, one copy each, counted (freed when the last body using it goes) */
const SHARED = new Map<string, { p: Promise<THREE.Texture>; n: number }>();
/** a picture no bigger than `cap` on its longer side (redrawn smaller; the same texture object) */
function shrink(t: THREE.Texture, cap: number): THREE.Texture {
  const img = t.image as { width: number; height: number } | undefined;
  if (!img || Math.max(img.width, img.height) <= cap) return t;
  const k = cap / Math.max(img.width, img.height), c = document.createElement('canvas');
  c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
  c.getContext('2d')!.drawImage(img as CanvasImageSource, 0, 0, c.width, c.height);
  t.image = c; t.needsUpdate = true;
  return t;
}

function greyHair(t: THREE.Texture): THREE.Texture {
  const img = t.image as HTMLImageElement;
  const c = document.createElement('canvas');
  c.width = img.width; c.height = img.height;
  const g = c.getContext('2d', { willReadFrequently: true })!;
  g.drawImage(img, 0, 0);
  const d = g.getImageData(0, 0, c.width, c.height), px = d.data;
  // the brightness of the strands (ignoring the see-through gaps), so the brightest ~10% become near white
  const hist = new Uint32Array(256);
  let n = 0;
  for (let i = 0; i < px.length; i += 4) if (px[i + 3] > 128) { hist[Math.round(0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2])]++; n++; }
  let acc = 0, top = 255;
  for (let v = 255; v >= 0; v--) { acc += hist[v]; if (acc >= n * 0.1) { top = Math.max(1, v); break; } }
  const k = 235 / top;
  for (let i = 0; i < px.length; i += 4) {
    // squeezed into the light end (0.5 to 0.95): the strands still show, without dark speckles through blonde
    const l = 128 + 0.48 * Math.min(255, (0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2]) * k);
    px[i] = px[i + 1] = px[i + 2] = l;
  }
  g.putImageData(d, 0, 0);
  const out = new THREE.CanvasTexture(c);
  out.wrapS = t.wrapS; out.wrapT = t.wrapT;
  t.dispose();
  return out;
}

/** each eye's box (left, right: split at the middle), and how far forward its front is */
function eyeBoxes(v: Float32Array): { x0: number; x1: number; y0: number; y1: number; z: number }[] {
  const boxes = [{ x0: Infinity, x1: -Infinity, y0: Infinity, y1: -Infinity, z: -Infinity }, { x0: Infinity, x1: -Infinity, y0: Infinity, y1: -Infinity, z: -Infinity }];
  for (let i = 0; i < v.length; i += 3) {
    const b = boxes[v[i] < 0 ? 0 : 1];
    b.x0 = Math.min(b.x0, v[i]); b.x1 = Math.max(b.x1, v[i]); b.y0 = Math.min(b.y0, v[i + 1]); b.y1 = Math.max(b.y1, v[i + 1]); b.z = Math.max(b.z, v[i + 2]);
  }
  return boxes.filter((b) => b.x0 < b.x1);
}
